/**
 * Test a single MCP tool call against a real organisation link. Expressions
 * are not resolved in a test run; edits stay local and never mutate the node.
 */

import { FC, useState, useCallback, useEffect } from 'react'
import { useGetList, useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Fade from '@mui/material/Fade'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Skeleton from '@mui/material/Skeleton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import TimerIcon from '@mui/icons-material/Timer'
import { themeColor, xxllncColor } from '../../../layout/theme/color'
import { getJson, getUrl, postJson, putJson } from '../../../providers/data'
import { OrganizationOut, OrgSeatOption, ServerEnvironmentOption, WorkflowToolCallTestResult } from '../../../types/apiRequests'
import { JsonValue, ToolCallNodeData } from '../types'
import { valueToText } from '../config/ParameterValueField'
import { looksLikeJsonTemplate } from '../config/parameterUtils'
import DisclosureSection from './DisclosureSection'
import FieldChips, { FieldPickResult } from './FieldChips'

interface TestToolCallDialogProps {
  open: boolean
  onClose: () => void
  nodeData: ToolCallNodeData
  nodeId: string
  workflowId?: string
  testConfig?: Record<string, unknown>
  /** Reports the response field paths of a successful test run. */
  onTestSuccess?: (fields: string[]) => void
  /** Type/description/required per declared parameter, for the input fields. */
  paramInfo?: Record<string, { type: string; description: string; required: boolean }>
}

const CATEGORY_MESSAGE_KEYS: Record<string, string> = {
  auth: 'workflow.test.errorAuth',
  forbidden: 'workflow.test.errorForbidden',
  // eslint-disable-next-line camelcase
  not_found: 'workflow.test.errorNotFound',
  validation: 'workflow.test.errorValidation',
  upstream: 'workflow.test.errorUpstream',
  network: 'workflow.test.errorNetwork',
}

interface TestParamState {
  text: string
  /** Original node expression — shown as a hint. */
  fromExpression?: string
}

// Expression values seed an empty field: expressions don't resolve in a test.
const seedParams = (
  params: ToolCallNodeData['parameters'],
  paramInfo?: Record<string, { type: string; description: string; required: boolean }>,
): Record<string, TestParamState> => {
  const seeded: Record<string, TestParamState> = {}
  Object.entries(params ?? {}).forEach(([key, mapping]) => {
    if (typeof mapping !== 'object' || mapping === null) return
    const text = valueToText(mapping.value)
    // eslint-disable-next-line security/detect-object-injection
    seeded[key] = text.includes('{{') ? { text: '', fromExpression: text } : { text }
  })
  Object.keys(paramInfo ?? {}).forEach((name) => {
    if (!(name in seeded)) seeded[name] = { text: '' } // eslint-disable-line security/detect-object-injection
  })
  return seeded
}

const toJsonValue = (text: string, paramType: string): JsonValue => {
  const trimmed = text.trim()
  if (paramType !== 'string' || looksLikeJsonTemplate(trimmed)) {
    try {
      return JSON.parse(trimmed) as JsonValue
    } catch {
      // Not valid JSON — send the raw text and let the tool validate.
    }
  }
  return text
}

const isJsonType = (paramType?: string): boolean =>
  paramType === 'object' || paramType === 'array'

/** Non-empty object/array field whose text is not parseable JSON. */
const hasInvalidJson = (state: TestParamState, paramType?: string): boolean => {
  if (!isJsonType(paramType) || state.text.trim() === '') return false
  try {
    JSON.parse(state.text)
    return false
  } catch {
    return true
  }
}

const TestToolCallDialog: FC<TestToolCallDialogProps> = ({
  open,
  onClose,
  nodeData,
  nodeId,
  workflowId,
  testConfig,
  onTestSuccess,
  paramInfo,
}) => {
  const translate = useTranslate()
  const [selectedOrgId, setSelectedOrgId] = useState('')
  const [selectedSeatId, setSelectedSeatId] = useState('')
  const [selectedEnvId, setSelectedEnvId] = useState('')
  const [environments, setEnvironments] = useState<ServerEnvironmentOption[]>([])
  const [seats, setSeats] = useState<OrgSeatOption[]>([])
  const [testParams, setTestParams] = useState<Record<string, TestParamState>>(() =>
    seedParams(nodeData.parameters, paramInfo),
  )
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<WorkflowToolCallTestResult | null>(null)

  const { data: organizations, isLoading: orgsLoading } = useGetList<OrganizationOut>(
    'organization',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'display_name', order: 'ASC' },
    },
    { enabled: open },
  )

  // seedGen remounts the parameter fields each open so re-seeded values show up.
  const [seedGen, setSeedGen] = useState(0)

  // Keyed on `open` only — re-seeding on every keystroke would wipe in-dialog edits.
  useEffect(() => {
    if (open) {
      setTestParams(seedParams(nodeData.parameters, paramInfo))
      setResult(null)
      setSeedGen((g) => g + 1)
    }
  }, [open]) // eslint-disable-line

  useEffect(() => {
    if (open && nodeData.serverId) {
      void getJson<ServerEnvironmentOption[]>(`${getUrl()}/workflow/server_environments/${nodeData.serverId}`)
        .then(setEnvironments)
        .catch(() => setEnvironments([]))
    }
  }, [open, nodeData.serverId])

  useEffect(() => {
    if (open && selectedOrgId) {
      setSelectedSeatId('')
      void getJson<OrgSeatOption[]>(`${getUrl()}/workflow/org_seats/${selectedOrgId}`)
        .then(setSeats)
        .catch(() => setSeats([]))
    } else {
      setSeats([])
    }
  }, [open, selectedOrgId])

  useEffect(() => {
    if (open && testConfig) {
      if (testConfig.organization_id) setSelectedOrgId(testConfig.organization_id as string)
      if (testConfig.environment_id) setSelectedEnvId(testConfig.environment_id as string)
    }
  }, [open, testConfig])

  // Pre-select when there is exactly one option; a testConfig value wins.
  useEffect(() => {
    if (!open || orgsLoading) return
    if (!selectedOrgId && organizations?.length === 1 && !testConfig?.organization_id) {
      setSelectedOrgId(organizations[0].id)
    }
  }, [open, orgsLoading, organizations, selectedOrgId, testConfig])

  useEffect(() => {
    if (!open) return
    if (!selectedEnvId && environments.length === 1 && !testConfig?.environment_id) {
      setSelectedEnvId(environments[0].id)
    }
  }, [open, environments, selectedEnvId, testConfig])

  const handleExecute = useCallback(async () => {
    if (!selectedOrgId) return
    setLoading(true)
    setResult(null)

    const payloadParams: Record<string, JsonValue> = {}
    Object.entries(testParams).forEach(([key, state]) => {
      // Empty fields are omitted, not sent as "".
      if (state.text === '') return
      const paramType = paramInfo?.[key]?.type ?? 'string' // eslint-disable-line security/detect-object-injection
      payloadParams[key] = toJsonValue(state.text, paramType) // eslint-disable-line security/detect-object-injection
    })
    try {
      const res = await postJson<WorkflowToolCallTestResult>(`${getUrl()}/workflow/test_tool_call`, {
        // eslint-disable-next-line camelcase
        organization_id: selectedOrgId,
        // eslint-disable-next-line camelcase
        server_id: nodeData.serverId,
        // eslint-disable-next-line camelcase
        tool_name: nodeData.toolName,
        parameters: payloadParams,
        // eslint-disable-next-line camelcase
        environment_id: selectedEnvId || undefined,
        // eslint-disable-next-line camelcase
        seat_id: selectedSeatId || undefined,
      })
      setResult(res)
      if (res.success && res.response_fields && res.response_fields.length > 0) {
        onTestSuccess?.(res.response_fields)
      }
      if (workflowId) {
        void putJson(`${getUrl()}/workflow/${workflowId}`, {
          // eslint-disable-next-line camelcase
          test_config: { organization_id: selectedOrgId, environment_id: selectedEnvId },
        }).catch(() => undefined) // silently ignore save errors
      }
    } catch (e: unknown) {
      setResult({
        success: false,
        response: {},
        // eslint-disable-next-line camelcase
        response_fields: [],
        error: e instanceof Error ? e.message : 'Request failed',
        // eslint-disable-next-line camelcase
        error_category: 'network',
        // eslint-disable-next-line camelcase
        elapsed_ms: 0,
      })
    } finally {
      setLoading(false)
    }
  }, [selectedOrgId, selectedSeatId, selectedEnvId, nodeData, testParams, paramInfo, workflowId, onTestSuccess])

  const copyFieldExpression = (_field: string, expression: string): FieldPickResult => {
    void navigator.clipboard.writeText(expression)
    return 'copied'
  }

  const elapsed = result?.elapsed_ms ?? 0
  const errorCategoryKey = result?.error_category
    ? CATEGORY_MESSAGE_KEYS[result.error_category]
    : undefined

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
          {translate('workflow.test.title')}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontFamily: 'monospace' }}
        >
          {nodeData.toolName || ''}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {loading && <LinearProgress />}

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {orgsLoading ? (
          <Skeleton variant="rounded" height={40} />
        ) : (
          <Box>
            <TextField
              select
              label={translate('workflow.test.organization')}
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              size="small"
              fullWidth
            >
              {organizations?.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.display_name}
                </MenuItem>
              ))}
            </TextField>
            <Typography
              variant="caption"
              sx={{ color: themeColor.slate50, display: 'block', mt: 0.5, lineHeight: 1.4 }}
            >
              {translate('workflow.test.orgHelp')}
            </Typography>
          </Box>
        )}

        {seats.length > 0 && (
          <TextField
            select
            label={translate('workflow.test.seat')}
            value={selectedSeatId}
            onChange={(e) => setSelectedSeatId(e.target.value)}
            size="small"
            fullWidth
          >
            <MenuItem value="">
              <em>{translate('workflow.test.noSeat')}</em>
            </MenuItem>
            {seats.map((seat) => (
              <MenuItem key={seat.id} value={seat.id}>
                {seat.auth0_email}
              </MenuItem>
            ))}
          </TextField>
        )}

        {environments.length > 0 && (
          <TextField
            select
            label={translate('workflow.test.environment')}
            value={selectedEnvId}
            onChange={(e) => setSelectedEnvId(e.target.value)}
            size="small"
            fullWidth
          >
            <MenuItem value="">
              <em>{translate('workflow.test.defaultEnvironment')}</em>
            </MenuItem>
            {environments.map((env) => (
              <MenuItem key={env.id} value={env.id}>
                {env.display_name}
              </MenuItem>
            ))}
          </TextField>
        )}

        {Object.keys(testParams).length > 0 && (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}>
              {translate('workflow.config.parameters')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {Object.entries(testParams).map(([key, state]) => {
                const info = paramInfo?.[key] // eslint-disable-line security/detect-object-injection
                const paramType = info?.type ?? 'string'
                const jsonField = isJsonType(paramType)
                const invalidJson = hasInvalidJson(state, paramType)
                const helper = [
                  invalidJson ? translate('workflow.test.invalidJson') : null,
                  jsonField ? `JSON (${paramType})` : null,
                  info?.description,
                  state.fromExpression
                    ? translate('workflow.test.seededFromExpression', {
                      expression: state.fromExpression,
                    })
                    : null,
                ]
                  .filter(Boolean)
                  .join(' — ')
                const onText = (text: string) =>
                  setTestParams((prev) => ({ ...prev, [key]: { ...state, text } }))

                if (paramType === 'boolean') {
                  return (
                    <TextField
                      key={`${key}-${seedGen}`}
                      select
                      label={key}
                      required={info?.required ?? false}
                      value={state.text === 'true' || state.text === 'false' ? state.text : ''}
                      onChange={(e) => onText(e.target.value)}
                      size="small"
                      fullWidth
                      helperText={helper || undefined}
                    >
                      <MenuItem value="">
                        <em>{translate('workflow.test.omitValue')}</em>
                      </MenuItem>
                      <MenuItem value="true">true</MenuItem>
                      <MenuItem value="false">false</MenuItem>
                    </TextField>
                  )
                }

                return (
                  <TextField
                    key={`${key}-${seedGen}`}
                    label={key}
                    required={info?.required ?? false}
                    type={paramType === 'integer' || paramType === 'number' ? 'number' : 'text'}
                    value={state.text}
                    onChange={(e) => onText(e.target.value)}
                    size="small"
                    fullWidth
                    multiline={jsonField}
                    minRows={jsonField ? 3 : undefined}
                    placeholder={state.fromExpression ?? (jsonField ? '{ … }' : undefined)}
                    error={invalidJson}
                    helperText={helper || undefined}
                    InputProps={
                      jsonField
                        ? { sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 } }
                        : undefined
                    }
                  />
                )
              })}
            </Box>
          </Box>
        )}

        <Button
          variant="contained"
          size="small"
          startIcon={<PlayArrowIcon />}
          onClick={() => void handleExecute()}
          disabled={
            !selectedOrgId ||
            loading ||
            !nodeData.toolName ||
            Object.entries(testParams).some(([key, state]) =>
              hasInvalidJson(state, paramInfo?.[key]?.type), // eslint-disable-line security/detect-object-injection
            )
          }
          sx={{ textTransform: 'none', alignSelf: 'flex-start', fontWeight: 600 }}
        >
          {translate('workflow.test.execute')}
        </Button>

        {result && (
          <Fade in timeout={200}>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1.5,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: result.success ? 'rgba(0,115,77,0.06)' : 'rgba(194,66,66,0.06)',
                  border: '1px solid',
                  borderColor: result.success ? xxllncColor.opGreen20 : xxllncColor.opRed20,
                }}
              >
                {result.success ? (
                  <CheckCircleIcon sx={{ fontSize: 16, color: xxllncColor.green100 }} />
                ) : (
                  <ErrorOutlineIcon sx={{ fontSize: 16, color: xxllncColor.red100 }} />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    color: result.success ? xxllncColor.green100 : xxllncColor.red100,
                    flex: 1,
                  }}
                >
                  {result.success ? translate('workflow.test.success') : translate('workflow.test.failed')}
                </Typography>
                {elapsed > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TimerIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace' }}>
                      {elapsed < 1000 ? `${elapsed}ms` : `${(elapsed / 1000).toFixed(1)}s`}
                    </Typography>
                  </Box>
                )}
              </Box>

              {!result.success && (
                <Box
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    borderRadius: 1.5,
                    bgcolor: 'rgba(0,0,0,0.03)',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {errorCategoryKey ? (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.25 }}>
                        {translate(errorCategoryKey)}
                        {result.status_code ? (
                          <Typography
                            component="span"
                            sx={{ ml: 0.75, fontFamily: 'monospace', fontSize: '0.72rem', color: themeColor.slate50 }}
                          >
                            HTTP {result.status_code}
                          </Typography>
                        ) : null}
                      </Typography>
                      {result.error && (
                        <DisclosureSection title={translate('workflow.test.errorDetails')}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              color: xxllncColor.red100,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all',
                              maxHeight: 180,
                              overflow: 'auto',
                            }}
                          >
                            {result.error}
                          </Typography>
                        </DisclosureSection>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: xxllncColor.red100 }}>
                      {result.error}
                    </Typography>
                  )}
                </Box>
              )}

              {result.success && (
                <>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1.5,
                      bgcolor: 'rgba(0,0,0,0.02)',
                      border: '1px solid',
                      borderColor: 'divider',
                      maxHeight: 250,
                      overflow: 'auto',
                      mb: 1.5,
                    }}
                  >
                    <pre style={{
                      margin: 0, fontSize: '0.75rem', lineHeight: 1.6,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                      fontFamily: 'monospace', color: '#28282C',
                    }}>
                      {JSON.stringify(result.response, null, 2)}
                    </pre>
                  </Box>

                  {result.response_fields && result.response_fields.length > 0 && (
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.75, display: 'block' }}>
                        {translate('workflow.test.availableFields')}
                      </Typography>
                      <FieldChips
                        fields={result.response_fields}
                        expressionFor={(field) => `{{ nodes.${nodeId}.output.${field} }}`}
                        onPick={copyFieldExpression}
                        pickedTooltip={() => translate('workflow.test.copied')}
                        action="copy"
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Fade>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default TestToolCallDialog
